import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  first_name: "",
  last_name: "",
  distributor_name: "",
  distributor_location: "",
  email: "",
  phone: "",
};

/**
 * PublicRegisterPage — anonymous distributor registration form.
 * URL: /register/:agencyId
 * Posts to public `/api/registrations`. Submission lands in the agency's
 * pending queue and the agency_user / super admin will approve or deny.
 */
export default function PublicRegisterPage() {
  const { agencyId } = useParams();
  const [agency, setAgency] = useState(null);
  const [agencyError, setAgencyError] = useState(false);
  const [agencyLoading, setAgencyLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setAgencyLoading(true);
    setAgencyError(false);
    axios
      .get(`${API}/rep-agencies/${agencyId}/public`)
      .then((res) => {
        if (!cancelled) setAgency(res.data);
      })
      .catch(() => {
        if (!cancelled) setAgencyError(true);
      })
      .finally(() => {
        if (!cancelled) setAgencyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agencyId]);

  const submit = async (e) => {
    e.preventDefault();
    for (const k of [
      "first_name",
      "last_name",
      "distributor_name",
      "distributor_location",
      "email",
      "phone",
    ]) {
      if (!form[k].trim()) {
        toast.error(`${k.replace("_", " ")} is required`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/registrations`, {
        agency_id: agencyId,
        ...form,
      });
      setDone(true);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "We couldn't submit your registration.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (agencyError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div
          className="max-w-md w-full bg-white border border-border rounded-sm p-8 text-center"
          data-testid="registration-invalid"
        >
          <div className="w-12 h-12 mx-auto bg-rose-100 text-rose-700 rounded-sm flex items-center justify-center">
            <Building2 className="w-6 h-6" />
          </div>
          <h1 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900">
            Invalid registration link
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            This link doesn't match an active rep agency. Double-check the URL,
            or ask whoever shared it to send you a fresh one.
          </p>
          <Link
            to="/login"
            className="mt-5 inline-block text-xs text-amber-700 hover:text-amber-800 font-mono-data uppercase tracking-widest"
          >
            Go to sign-in
          </Link>
        </div>
      </div>
    );
  }

  if (done) {    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div
          className="max-w-md w-full bg-white border border-border rounded-sm p-8 text-center"
          data-testid="registration-success"
        >
          <div className="w-12 h-12 mx-auto bg-emerald-100 text-emerald-700 rounded-sm flex items-center justify-center">
            <Check className="w-6 h-6" />
          </div>
          <h1 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900">
            Request received
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Thanks, {form.first_name}. Your registration is in the queue at{" "}
            <strong>{agency?.name || "your rep agency"}</strong>. You'll hear
            from them soon — most agencies respond within one business day.
          </p>
          <div className="mt-6 text-[11px] text-muted-foreground font-mono-data uppercase tracking-widest">
            What happens next
          </div>
          <ol className="mt-2 text-left text-sm text-slate-700 space-y-1.5">
            <li>1. The rep agency reviews your request</li>
            <li>2. You'll receive an email with your login</li>
            <li>3. Log in and start searching products</li>
          </ol>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div
        className="max-w-md mx-auto bg-white border border-border rounded-sm p-6"
        data-testid="public-register-page"
      >
        <div className="text-center pb-4 border-b border-border">
          <div className="w-12 h-12 mx-auto bg-slate-900 text-white rounded-sm flex items-center justify-center">
            {agency?.logo_url ? (
              <img
                src={agency.logo_url}
                alt={agency?.name}
                className="w-full h-full object-contain p-1.5"
              />
            ) : (
              <Building2 className="w-5 h-5" />
            )}
          </div>
          <div className="mt-2 text-[11px] uppercase tracking-widest text-amber-600 font-mono-data">
            Distributor registration
          </div>
          <h1 className="mt-0.5 font-display text-xl font-bold tracking-tight text-slate-900">
            Request access to {agency?.name || "your rep agency's portal"}
          </h1>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Once approved, you'll be able to search and find products from the
            agency's manufacturer portfolio.
          </p>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="First name"
              value={form.first_name}
              onChange={(v) => setForm({ ...form, first_name: v })}
              testId="reg-first-name"
            />
            <Field
              label="Last name"
              value={form.last_name}
              onChange={(v) => setForm({ ...form, last_name: v })}
              testId="reg-last-name"
            />
          </div>
          <Field
            label="Distributor name"
            value={form.distributor_name}
            onChange={(v) => setForm({ ...form, distributor_name: v })}
            testId="reg-distributor-name"
            placeholder="e.g., Capital Electric Supply"
          />
          <Field
            label="Distributor location"
            value={form.distributor_location}
            onChange={(v) => setForm({ ...form, distributor_location: v })}
            testId="reg-distributor-location"
            placeholder="City, State"
          />
          <Field
            label="Email"
            type="email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            testId="reg-email"
          />
          <Field
            label="Phone"
            value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
            testId="reg-phone"
          />

          <Button
            type="submit"
            disabled={submitting}
            className="w-full rounded-sm bg-slate-900 hover:bg-slate-800"
            data-testid="reg-submit-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Submit registration
          </Button>
          <p className="text-[10px] text-center text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="underline hover:text-slate-900">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text", placeholder }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
        {label} *
      </Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 rounded-sm"
        data-testid={testId}
      />
    </div>
  );
}
